/**
 * Weigh-ins and body composition.
 * The water-weight engine (@engine/water, same file the Worker uses) estimates
 * how much short-term water each morning's weigh-in carries from recent carbs
 * and salt, subtracts it, and smooths the rest into a "true weight" line.
 */
import { useMemo, useState, type ReactElement } from 'react';
import { computeWater, type WaterBreakdown, type WaterResult } from '@engine/water';
import { AddWeightModal } from '../components/AddWeightModal';
import { Icon } from '../components/Icon';
import { SaltModal } from '../components/SaltModal';
import { TimeChart } from '../components/TimeChart';
import { Card, Empty, Segmented, Skeleton, StatTile } from '../components/ui';
import { addDays, formatDayShort, formatTime } from '../lib/dates';
import { BODY_SOURCE_LABEL } from '../lib/labels';
import { errorMessage } from '../lib/logger';
import type { BodyStat, DailyMetric, UnitSystem } from '../lib/types';
import { emaTrend } from '../lib/trend';
import { massFromKg, massUnit } from '../lib/units';
import { useApp, useAsync } from '../state/app';

type Range = 30 | 90 | 180 | 365;

export function BodyPage(): ReactElement {
  const { source, profile, today, day, version, bump, toast } = useApp();
  const units = profile.unit_system;
  const [range, setRange] = useState<Range>(90);
  const [adding, setAdding] = useState<boolean>(false);
  const [salting, setSalting] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  // Load extra history before the window so the trend line is warmed up.
  const { data, error, loading } = useAsync<BodyStat[]>(() => source.listBodyStats(addDays(today, -(range + 60)), today), [source, today, range, version]);
  const all: BodyStat[] = data ?? [];
  const from: string = addDays(today, -range);
  const inRange: BodyStat[] = all.filter((s) => s.local_date >= from);
  const metrics = useAsync<DailyMetric[]>(() => source.listDailyMetrics(addDays(today, -(range + 60)), today), [source, today, range, version]);
  const latestKg: number | null = all[0]?.weight_kg ?? null;
  // After 8 pm we assume today's food is all logged; the checkbox on the water card overrides this.
  const [todayDone, setTodayDone] = useState<boolean>(() => new Date().getHours() >= 20);
  const cycleStart: string | null = profile.cycle_last_period_start;
  const cycleLength: number | null = profile.cycle_length_days;
  const water: WaterResult | null = useMemo(() => {
    if (!metrics.data) return null;
    return computeWater({
      today,
      todayComplete: todayDone,
      bodyWeightKg: latestKg,
      cycle: cycleStart && cycleLength ? { lastPeriodStart: cycleStart, cycleLengthDays: cycleLength } : null,
      days: metrics.data.map((d) => ({ date: d.day, logged: d.meal_count > 0, carbsG: d.carbs_g, sodiumMg: d.sodium_mg, potassiumMg: d.potassium_mg, weightKg: d.weight_kg })),
    });
  }, [metrics.data, today, latestKg, todayDone, cycleStart, cycleLength]);
  const waterOk: boolean = water !== null && water.status === 'ok';

  const rawTrend = waterOk
    ? water!.trueWeight.filter((p) => p.date >= from).map((p) => ({ day: p.date, y: massFromKg(p.rawTrendKg, units) }))
    : emaTrend(all.map((s) => ({ day: s.local_date, weight: s.weight_kg, measuredAt: s.measured_at }))).filter((p) => p.day >= from).map((p) => ({ day: p.day, y: massFromKg(p.y, units) }));
  const trueLine = waterOk ? water!.trueWeight.filter((p) => p.date >= from).map((p) => ({ day: p.date, y: massFromKg(p.trueKg, units) })) : [];
  const trend = trueLine.length > 0 ? trueLine : rawTrend;
  const raw = [...inRange].reverse().map((s) => ({ day: s.local_date, y: massFromKg(s.weight_kg, units) }));
  const latest: BodyStat | undefined = all[0];
  const trendNow: number | null = trend[trend.length - 1]?.y ?? null;
  const trendWeekAgo = trend.filter((p) => p.day <= addDays(today, -7)).pop()?.y ?? null;
  const weekly: number | null = trendNow !== null && trendWeekAgo !== null ? trendNow - trendWeekAgo : null;
  const lastBf: BodyStat | undefined = all.find((s) => s.body_fat_pct !== null);
  const pred = waterOk ? water!.prediction : null;
  const predLine = pred ? [{ day: pred.date, y: massFromKg(pred.weightKg, units) }] : [];
  const predBand = pred && trueLine.length > 0
    ? { color: 'var(--brand)', points: [{ day: trueLine[trueLine.length - 1]!.day, lo: trueLine[trueLine.length - 1]!.y, hi: trueLine[trueLine.length - 1]!.y }, { day: pred.date, lo: massFromKg(pred.lowKg, units), hi: massFromKg(pred.highKg, units) }] }
    : undefined;
  const waterToday: WaterBreakdown | null = waterOk ? water!.today : null;

  const remove = async (id: number): Promise<void> => {
    try { await source.deleteBodyStat(id); toast('Weigh-in deleted'); bump(); } catch (err) { toast(errorMessage(err), 'err'); } finally { setDeleting(null); }
  };

  return (
    <div className="stack">
      <div className="tiles">
        <StatTile label={trueLine.length > 0 ? 'True weight' : 'Trend'} value={trendNow !== null ? trendNow.toFixed(1) : '—'} unit={massUnit(units)}
          delta={weekly !== null ? `${weekly > 0 ? '+' : ''}${weekly.toFixed(2)} ${massUnit(units)} vs last week` : latest ? `scale ${massFromKg(latest.weight_kg, units).toFixed(1)} · ${formatDayShort(latest.local_date)}` : 'no weigh-ins yet'}
          deltaClass={weekly === null ? 'muted' : weekly < 0 ? 'good' : 'warn'} />
        <StatTile label="Water today" value={waterToday ? signed(massFromKg(waterToday.totalG / 1000, units), 1) : '—'} unit={massUnit(units)}
          delta={waterToday ? waterSummary(waterToday, units) : 'log food on 7 days to start'} />
        <StatTile label="Tomorrow’s scale" value={pred ? massFromKg(pred.weightKg, units).toFixed(1) : '—'} unit={massUnit(units)}
          delta={pred ? `likely ${massFromKg(pred.lowKg, units).toFixed(1)}–${massFromKg(pred.highKg, units).toFixed(1)}` : 'needs 3+ weigh-ins'} />
        <StatTile label="Body fat" value={lastBf?.body_fat_pct !== null && lastBf?.body_fat_pct !== undefined ? lastBf.body_fat_pct.toFixed(1) : '—'} unit="%" delta={lastBf ? `${BODY_SOURCE_LABEL[lastBf.source]} · ${formatDayShort(lastBf.local_date)}` : 'optional'} />
      </div>
      <Card title="Weight trend" right={
        <>
          <Segmented<Range> options={[{ value: 30, label: '30d' }, { value: 90, label: '90d' }, { value: 180, label: '6m' }, { value: 365, label: '1y' }]} value={range} onChange={setRange} />
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setAdding(true)}><Icon name="plus" size={16} /> Weigh-in</button>
        </>
      }>
        {error !== null && <div className="note err">{error}</div>}
        {loading && data === null ? <Skeleton height={300} /> : (
          <TimeChart height={300} emptyText="No weigh-ins in this range"
            lines={[
              { name: 'Weigh-in', color: 'var(--text-3)', points: raw, dots: true, hideLine: true },
              ...(trueLine.length > 0 ? [{ name: 'Scale trend', color: 'var(--text-3)', points: rawTrend, width: 1.5, dashed: true }] : []),
              { name: trueLine.length > 0 ? 'True weight' : 'Trend', color: 'var(--protein)', points: trend, width: 3 },
              ...(predLine.length > 0 ? [{ name: 'Tomorrow', color: 'var(--brand)', points: predLine, dots: true, hideLine: true }] : []),
            ]}
            band={predBand}
            yFormat={(v) => v.toFixed(0)} />
        )}
        <p className="faint" style={{ fontSize: 12.5, fontWeight: 600, margin: '8px 0 0' }}>
          {trueLine.length > 0
            ? <>Dots are weigh-ins. The <b style={{ color: 'var(--protein)' }}>blue line</b> is your true weight: each weigh-in minus the water from recent carbs and salt, then smoothed. The dashed line is the plain scale trend, for comparison. The <b style={{ color: 'var(--brand-ink)' }}>gold dot</b> is tomorrow morning’s likely weight.</>
            : <>Dots are single weigh-ins (they jump with water and food). The line is a 10-day smoothed trend, which is the number to watch.</>}
        </p>
      </Card>
      <WaterCard water={water} units={units} loading={metrics.loading && metrics.data === null} todayDone={todayDone} onTodayDone={setTodayDone} onAddSalt={() => setSalting(true)} />
      <Card title="Log" sub={`${inRange.length} in the last ${range} days`}>
        {inRange.length === 0 ? <Empty title="No weigh-ins yet">Use <b>Weigh-in</b> above. Mornings, before food, work best.</Empty> : (
          <div style={{ overflowX: 'auto' }}>
            <table className="draft-table">
              <thead><tr><th>Date</th><th>Weight</th><th>Body fat</th><th>Source</th><th>Notes</th><th /></tr></thead>
              <tbody>
                {inRange.map((s) => (
                  <tr key={s.id}>
                    <td style={{ fontWeight: 600 }}>{formatDayShort(s.local_date)} <span className="faint">{formatTime(s.measured_at)}</span></td>
                    <td className="num" style={{ fontWeight: 700 }}>{massFromKg(s.weight_kg, units).toFixed(1)} {massUnit(units)}</td>
                    <td className="num">{s.body_fat_pct !== null ? `${s.body_fat_pct}%` : '—'}</td>
                    <td>{BODY_SOURCE_LABEL[s.source]}{s.is_fasted ? <span className="faint"> · fasted</span> : null}</td>
                    <td className="faint">{s.notes ?? ''}</td>
                    <td style={{ textAlign: 'right' }}>
                      {deleting === s.id
                        ? <span className="row" style={{ justifyContent: 'flex-end', gap: 6 }}><button type="button" className="btn btn-sm" onClick={() => setDeleting(null)}>Keep</button><button type="button" className="btn btn-sm btn-danger" onClick={() => void remove(s.id)}>Delete</button></span>
                        : <button type="button" className="icon-btn" aria-label="Delete weigh-in" onClick={() => setDeleting(s.id)}><Icon name="trash" size={16} /></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {adding && <AddWeightModal day={day} onClose={() => setAdding(false)} onSaved={bump} />}
      {salting && <SaltModal onClose={() => setSalting(false)} />}
    </div>
  );
}

/** Sign from the rounded value, so -0.02 shows as 0.0, not -0.0. */
const signed = (v: number, digits: number): string => {
  const r: number = Number(v.toFixed(digits));
  return `${r > 0 ? '+' : r < 0 ? '−' : ''}${Math.abs(r).toFixed(digits)}`;
};

function waterSummary(w: WaterBreakdown, units: UnitSystem): string {
  const parts: string[] = [];
  if (w.carbsG !== null) parts.push(`carbs ${signed(massFromKg(w.carbsG / 1000, units), 1)}`);
  if (w.sodiumG !== null) parts.push(`salt ${signed(massFromKg(w.sodiumG / 1000, units), 1)}`);
  if (w.cycleG !== null && w.cycleG !== 0) parts.push(`cycle ${signed(massFromKg(w.cycleG / 1000, units), 1)}`);
  return parts.length > 0 ? parts.join(' · ') : 'no recent food logged';
}

/** One signed bar per water source, centred on zero. */
function WaterBar({ label, grams, maxG, color, units }: { label: string; grams: number | null; maxG: number; color: string; units: UnitSystem }): ReactElement {
  const pct: number = grams === null ? 0 : Math.min(50, (Math.abs(grams) / maxG) * 50);
  return (
    <div className="wbar">
      <span className="wbar-label">{label}</span>
      <span className="wbar-track">
        <i className="wbar-zero" />
        {grams !== null && <b style={{ background: color, width: `${pct}%`, left: grams >= 0 ? '50%' : `${50 - pct}%` }} />}
      </span>
      <span className="wbar-val num">{grams === null ? 'no data' : `${signed(massFromKg(grams / 1000, units), 2)} ${massUnit(units)}`}</span>
    </div>
  );
}

function WaterCard({ water, units, loading, todayDone, onTodayDone, onAddSalt }: { water: WaterResult | null; units: UnitSystem; loading: boolean; todayDone: boolean; onTodayDone: (v: boolean) => void; onAddSalt: () => void }): ReactElement {
  if (loading || water === null) return <Card title="Water weight"><Skeleton height={140} /></Card>;
  if (water.status !== 'ok' || water.today === null) {
    return (
      <Card title="Water weight">
        <Empty title="Not enough food logged yet">{water.warnings[0] ?? 'Log a week of meals and this card will show how much water you are carrying.'}</Empty>
      </Card>
    );
  }
  const t: WaterBreakdown = water.today;
  const maxG: number = Math.max(500, ...[t.carbsG, t.sodiumG, t.cycleG, water.tomorrow?.carbsG, water.tomorrow?.sodiumG].map((g) => Math.abs(g ?? 0)));
  const n = water.noise;
  return (
    <Card title="Water weight" sub="this morning, vs your normal">
      <div className="stack" style={{ gap: 10 }}>
        <WaterBar label="Carbs (glycogen)" grams={t.carbsG} maxG={maxG} color="var(--carbs)" units={units} />
        <WaterBar label="Salt vs potassium" grams={t.sodiumG} maxG={maxG} color="var(--burn)" units={units} />
        {t.cycleG !== null && <WaterBar label="Cycle" grams={t.cycleG} maxG={maxG} color="var(--fat)" units={units} />}
        <WaterBar label="Total" grams={t.totalG} maxG={maxG} color="var(--protein)" units={units} />
      </div>
      <p className="muted" style={{ fontSize: 13.5, margin: '12px 0 0' }}>
        {t.totalG > 150 ? 'You are probably holding a little extra water. It is not fat and should pass in a few days.'
          : t.totalG < -150 ? 'You are probably a little lighter on water than usual, so the scale may look better than the real trend.'
            : 'Water is close to your normal, so today’s scale is a fair reading.'}
        {t.loggedDays < 3 && ` Based on ${t.loggedDays} of the last 3 days (the rest weren’t logged).`}
      </p>
      {n !== null && (
        <div className="note" style={{ marginTop: 10 }}>
          Taking water out cut your day-to-day scale bounce from <b>±{massFromKg(n.rawSdKg, units).toFixed(1)}</b> to <b>±{massFromKg(n.adjustedSdKg, units).toFixed(1)} {massUnit(units)}</b>
          {n.explainedPct > 0 ? <> (it explains about <b>{Math.round(n.explainedPct)}%</b> of the bounce).</> : '. Most of your bounce comes from other things (food in transit, timing, the scale itself).'}
        </div>
      )}
      <label className="row" style={{ gap: 8, marginTop: 10, fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}>
        <input type="checkbox" checked={todayDone} onChange={(ev) => onTodayDone(ev.target.checked)} />
        I’m done logging food today <span className="faint">(counts today toward tomorrow’s scale)</span>
      </label>
      <div className="row" style={{ gap: 6, marginTop: 6, fontSize: 13.5, fontWeight: 600 }}>
        <span className="muted">Ate out, or salted your cooking?</span>
        <button type="button" className="link-btn" onClick={onAddSalt}>Add the salt</button>
      </div>
      <details style={{ marginTop: 12 }}>
        <summary className="faint" style={{ fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>How this is worked out</summary>
        <ul style={{ margin: '8px 0 10px', paddingLeft: 18, fontSize: 13 }}>
          {water.warnings.map((w) => <li key={w} className="warn" style={{ fontWeight: 600 }}>{w}</li>)}
          {water.assumptions.map((a) => <li key={a} className="muted">{a}</li>)}
          <li className="muted">Glycogen store for you: about {Math.round(water.store.glycogenG)} g, so carbs can move at most ±{massFromKg(water.store.maxWaterG / 1000, units).toFixed(1)} {massUnit(units)} of water.</li>
        </ul>
        <ol className="cite" style={{ paddingLeft: 18, margin: 0 }}>
          {water.citations.map((c) => <li key={c.key}>{c.text} <a href={`https://pubmed.ncbi.nlm.nih.gov/${c.pmid}/`} target="_blank" rel="noreferrer">PubMed</a></li>)}
        </ol>
      </details>
    </Card>
  );
}
